import ThemedSelect from "../components/shared/ThemedSelect.jsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Form, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ReportMasthead } from "../components/reports/ReportDocument.jsx";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { getInvoicingByMonth, listEntityRows, listUsers } from "../services/apiClient.js";
import { useMasterCompanyNames } from "../services/useMasterCompanies.js";
import { monthKey } from "../utils/date.js";
import "../styles/home.css";

// Invoicing by Month (Karen 2026-07-19; range UX ported from medispace's
// invoicing-by-month report): what was invoiced each calendar month of
// invoice_date, voided invoices excluded, paid-status irrelevant. Quick range
// presets fill the two month pickers (editing either flips to Custom); the
// report re-runs itself on a 250ms debounce — no Run button. Filters mirror
// Predictive Invoicing (Karen): division / PCO / job type / insurer, plus the
// include-excess toggle (Robert 2026-07-22). Detailed mode lists the invoices
// behind each month with real claim links (2.0 improvement over medispace's
// tooltips); the current month wears a "partial" badge.

const nzd = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", minimumFractionDigits: 2 });
const money = (v) => (v == null ? "—" : nzd.format(Number(v)));

// "2026-03" → "Mar 2026", built from parts so it stays on the NZ calendar (a
// toISOString round-trip walks the 1st of the month back into the one before).
const monthText = (ym) => {
  const [y, m] = String(ym || "").split("-");
  if (!y || !m) return "";
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-NZ", { month: "short", year: "numeric" });
};

const PRESETS = [
  { key: "3", label: "Last 3 months", months: 3 },
  { key: "6", label: "Last 6 months", months: 6 },
  { key: "9", label: "Last 9 months", months: 9 },
  { key: "12", label: "Last 12 months", months: 12 },
  { key: "custom", label: "Custom", months: null },
];

function toCsv(months) {
  const cols = [
    ["Month", (m) => m.label],
    ["Invoices", (m) => m.invoice_count],
    ["Claims", (m) => m.claim_count],
    ["Subtotal", (m) => m.subtotal],
    ["GST", (m) => m.gst],
    ["Total", (m) => m.total],
    ["Avg / Invoice", (m) => m.avg_per_invoice],
  ];
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [cols.map(([h]) => h).join(","), ...months.map((m) => cols.map(([, f]) => esc(f(m))).join(","))].join("\n");
}

function InvoicingByMonthPage() {
  // Master companies are data now (Owner Data); same name keeps the diff and
  // the saved-search values identical to the old config constant.
  const COMPANY_GROUP_KEYS = useMasterCompanyNames();
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [users, setUsers] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [insurers, setInsurers] = useState([]);

  const [preset, setPreset] = useState("3");
  const [from, setFrom] = useState(monthKey(-2));
  const [to, setTo] = useState(monthKey(0));
  const [divisionId, setDivisionId] = useState("");
  const [pmId, setPmId] = useState("");
  const [jobTypeId, setJobTypeId] = useState("");
  const [insurerId, setInsurerId] = useState("");
  const [includeExcess, setIncludeExcess] = useState(true);
  const [detailed, setDetailed] = useState(false);

  useEffect(() => {
    listEntityRows("divisions").then(setDivisions).catch(() => {});
    listUsers({ pco: true, activeOnly: true }).then(setUsers).catch(() => {});
    listEntityRows("job-types").then(setJobTypes).catch(() => {});
    listEntityRows("insurer-companies").then(setInsurers).catch(() => {});
  }, []);

  // A preset fills the pickers: to = the CURRENT month inclusive,
  // from = to − (n − 1). Custom leaves them alone.
  function applyPreset(key) {
    setPreset(key);
    const p = PRESETS.find((x) => x.key === key);
    if (p?.months) {
      setTo(monthKey(0));
      setFrom(monthKey(-(p.months - 1)));
    }
  }
  // Editing a picker by hand = Custom (medispace behaviour).
  const editMonth = (setter) => (e) => {
    setPreset("custom");
    setter(e.target.value);
  };

  // 250ms-debounced auto-run — no Run button (medispace behaviour).
  const timer = useRef(null);
  useEffect(() => {
    if (!/^\d{4}-\d{2}$/.test(from) || !/^\d{4}-\d{2}$/.test(to)) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setError("");
      getInvoicingByMonth({
        from,
        to,
        division_id: divisionId,
        project_coordinator_id: pmId,
        job_type_id: jobTypeId,
        insurer_id: insurerId,
        include_excess: includeExcess ? "1" : "0",
        detailed: detailed ? "1" : "0",
      })
        .then(setReport)
        .catch((err) => setError(err.response?.data?.error || "Unable to load the invoicing report."));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [from, to, divisionId, pmId, jobTypeId, insurerId, includeExcess, detailed]);

  const invoicesByMonth = useMemo(() => {
    if (!report?.invoices) return null;
    const map = new Map();
    for (const inv of report.invoices) {
      if (!map.has(inv.month)) map.set(inv.month, []);
      map.get(inv.month).push(inv);
    }
    return map;
  }, [report]);

  function exportCsv() {
    const blob = new Blob([toCsv(report.months)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "invoicing-by-month.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // What this copy covers, for the masthead. Insurer leads because that is the cut
  // anyone outside GPS asks for; a combined group keeps its "(combined)" label so
  // nobody reads it as one company's figures.
  const nameOf = (list, id, field = "name") =>
    (id && (list || []).find((r) => String(r.id) === String(id))?.[field]) || null;
  const insurerLabel = insurerId.startsWith("group:")
    ? `${insurerId.slice("group:".length)} (combined)`
    : nameOf(insurers, insurerId);
  const scopeLabel =
    insurerLabel || nameOf(divisions, divisionId) || nameOf(users, pmId, "full_name") || "All insurers";

  const t = report?.totals;
  return (
    <section className="page-panel">
      {/* App chrome: the document carries its own masthead (report-document.css). */}
      <div className="d-flex flex-wrap align-items-center column-gap-3 row-gap-2 mb-2 no-print">
        <div className="page-heading">
          <h2>Invoicing by Month <RulesHelp topic="invoicingByMonth" /></h2>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
          <ThemedSelect size="sm" style={{ width: 150 }} value={preset} onChange={(e) => applyPreset(e.target.value)}>
            {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </ThemedSelect>
          <Form.Control size="sm" type="month" style={{ width: 150 }} value={from} onChange={editMonth(setFrom)} />
          <span className="text-muted">to</span>
          <Form.Control size="sm" type="month" style={{ width: 150 }} value={to} onChange={editMonth(setTo)} />
          <Button size="sm" variant="outline-secondary" onClick={exportCsv} disabled={!report || !report.months.length}>
            Export CSV
          </Button>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3 no-print">
        <ThemedSelect size="sm" style={{ width: 150 }} value={divisionId} onChange={(e) => setDivisionId(e.target.value)}>
          <option value="">All divisions</option>
          {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </ThemedSelect>
        <ThemedSelect size="sm" style={{ width: 180 }} value={pmId} onChange={(e) => setPmId(e.target.value)}>
          <option value="">All project co-ordinators</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </ThemedSelect>
        <ThemedSelect size="sm" style={{ width: 160 }} value={jobTypeId} onChange={(e) => setJobTypeId(e.target.value)}>
          <option value="">All job types</option>
          {jobTypes.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
        </ThemedSelect>
        <ThemedSelect size="sm" style={{ width: 190 }} value={insurerId} onChange={(e) => setInsurerId(e.target.value)}>
          <option value="">All insurers</option>
          {COMPANY_GROUP_KEYS.map((g) => <option key={g} value={`group:${g}`}>{g} (combined)</option>)}
          <option disabled>──────────</option>
          {insurers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </ThemedSelect>
        <Form.Check
          type="switch" id="ibm-excess" label="Include excess invoices"
          checked={includeExcess} onChange={(e) => setIncludeExcess(e.target.checked)}
        />
        <Form.Check
          type="switch" id="ibm-detailed" label="Show invoices"
          checked={detailed} onChange={(e) => setDetailed(e.target.checked)}
        />
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {!report ? (
        <p className="text-muted">Loading…</p>
      ) : (
        // PORTRAIT on purpose (no `report-doc-wide`): seven columns fit an upright
        // sheet comfortably, and there is nothing to gain from turning the paper.
        <div className="report-doc border rounded">
          <ReportMasthead
            report="Invoicing by Month"
            title={scopeLabel}
            subtitle={`Grayburn Property Services · ${monthText(report.from)} to ${monthText(report.to)}${includeExcess ? "" : ", excess invoices excluded"}`}
          />
          <div className="report-body">
        <Table striped bordered hover responsive size="sm" className="mb-0 align-middle report-table">
          <thead className="table-surface">
            <tr>
              <th>Month</th>
              <th className="text-end">Invoices</th>
              <th className="text-end">Claims</th>
              <th className="text-end">Subtotal</th>
              <th className="text-end">GST</th>
              <th className="text-end">Total</th>
              <th className="text-end">Avg / Invoice</th>
            </tr>
          </thead>
          <tbody>
            {report.months.map((m) => (
              <MonthRows key={m.month} m={m} invoices={invoicesByMonth?.get(m.month)} />
            ))}
          </tbody>
          <tfoot className="table-surface">
            <tr>
              <td className="fw-semibold">Total</td>
              <td className="text-end fw-semibold">{t.invoice_count}</td>
              <td className="text-end fw-semibold">{t.claim_count}</td>
              <td className="text-end fw-semibold">{money(t.subtotal)}</td>
              <td className="text-end fw-semibold">{money(t.gst)}</td>
              <td className="text-end fw-semibold">{money(t.total)}</td>
              <td className="text-end fw-semibold">{money(t.avg_per_invoice)}</td>
            </tr>
          </tfoot>
        </Table>
          </div>
        </div>
      )}
    </section>
  );
}

function MonthRows({ m, invoices }) {
  const typeSummary = Object.entries(m.types)
    .map(([type, v]) => `${type}: ${v.invoice_count} — ${money(v.total)}`)
    .join("\n");
  return (
    <>
      <tr>
        <td className="text-nowrap" data-hint={typeSummary || undefined}>
          {m.label}
          {m.partial ? (
            <Badge bg="secondary" className="ms-2" data-hint="The current month — more invoices are still to come">
              partial
            </Badge>
          ) : null}
        </td>
        <td className="text-end">{m.invoice_count}</td>
        <td className="text-end">{m.claim_count}</td>
        <td className="text-end text-nowrap">{money(m.subtotal)}</td>
        <td className="text-end text-nowrap">{money(m.gst)}</td>
        <td className="text-end text-nowrap fw-semibold">{money(m.total)}</td>
        <td className="text-end text-nowrap">{money(m.avg_per_invoice)}</td>
      </tr>
      {(invoices || []).map((inv) => (
        <tr key={inv.id} className="table-borderless">
          <td className="ps-4 text-nowrap text-muted">
            <Link to={`/claims/${inv.claim_number}?tab=invoicing`}>{inv.claim_number}</Link>
            <span className="ms-2">{inv.client_name || ""}</span>
          </td>
          <td className="text-end text-muted text-nowrap">#{inv.invoice_no}</td>
          <td className="text-muted text-nowrap text-end" data-hint={inv.is_final ? "Final invoice" : undefined}>
            {inv.invoice_type}{inv.is_final ? " ✓" : ""}
          </td>
          <td className="text-end text-muted text-nowrap">{money(inv.subtotal)}</td>
          <td className="text-end text-muted text-nowrap">{money(inv.gst)}</td>
          <td className="text-end text-muted text-nowrap">{money(inv.total)}</td>
          <td className="text-end text-muted text-nowrap">{inv.invoice_date}</td>
        </tr>
      ))}
    </>
  );
}

export default InvoicingByMonthPage;
